import { useState } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import PokemonList from "./pages/PokemonList";
import PokemonDetail from "./pages/PokemonDetail";
import { PokedexNavProvider, usePokedexNav } from "./context/PokedexNavContext";
import type { PokemonSummary } from "./services/pokeApi";
import { device, gb } from "./styles/theme";

const AppContainer = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 28px 16px 20px;
  gap: 18px;
`;

const PokedexFrame = styled.div`
  width: min(920px, 100%);
  display: flex;
  background:
    linear-gradient(180deg, ${device.redLight} 0%, ${device.plastic} 18%, ${device.red} 52%, ${device.redDark} 100%);
  border: 4px solid ${device.redDeep};
  box-shadow:
    0 0 0 2px #3a070a,
    8px 12px 0 rgba(0, 0, 0, 0.35),
    inset 0 2px 0 rgba(255, 255, 255, 0.28),
    inset 0 -8px 16px rgba(0, 0, 0, 0.18);
  padding: 18px 16px 16px;
  gap: 0;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    inset: 8px;
    border: 2px solid rgba(92, 12, 16, 0.35);
    pointer-events: none;
  }

  @media (max-width: 760px) {
    flex-direction: column;
  }
`;

const Panel = styled.section`
  flex: 1;
  min-width: 0;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 52px;
  padding: 0 8px 8px;
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 3px #dfeff4, 0 0 10px ${device.lens}, inset -6px -8px 10px rgba(0, 0, 0, 0.25); }
  50% { box-shadow: 0 0 0 3px #dfeff4, 0 0 18px ${device.lens}, inset -6px -8px 10px rgba(0, 0, 0, 0.25); }
`;

const Lens = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background:
    radial-gradient(circle at 32% 30%, #e7f9ff 0 8%, ${device.lens} 28%, ${device.lensDeep} 72%, #06394c 100%);
  border: 3px solid ${device.redDeep};
  box-shadow:
    0 0 0 3px #dfeff4,
    0 0 12px ${device.lens},
    inset -6px -8px 10px rgba(0, 0, 0, 0.25);
  animation: ${pulse} 2.4s ease-in-out infinite;
  flex-shrink: 0;
`;

const Leds = styled.div`
  display: flex;
  gap: 8px;
`;

const Led = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${(props) => props.$color};
  border: 2px solid ${device.redDeep};
  box-shadow:
    inset 1px 1px 0 rgba(255, 255, 255, 0.7),
    0 0 6px ${(props) => props.$color};
`;

const ScreenBezel = styled.div`
  background: linear-gradient(180deg, ${device.bezelHi}, ${device.bezel});
  padding: 14px 14px 18px;
  margin: 0 6px;
  border: 3px solid #0d0f0d;
  box-shadow:
    inset 0 2px 0 rgba(255, 255, 255, 0.08),
    0 4px 0 rgba(0, 0, 0, 0.25);
  position: relative;

  &::after {
    content: "";
    position: absolute;
    left: 18px;
    right: 18px;
    bottom: 6px;
    height: 4px;
    background: repeating-linear-gradient(
      90deg,
      #111 0 8px,
      transparent 8px 12px
    );
    opacity: 0.7;
  }
`;

const scan = keyframes`
  0% { transform: translateY(-100%); }
  100% { transform: translateY(100%); }
`;

const Lcd = styled.div<{ $tall?: boolean }>`
  background: ${gb.lightest};
  border: 4px solid ${gb.dark};
  min-height: 380px;
  height: 380px;
  padding: 10px;
  position: relative;
  overflow: hidden;
  box-shadow:
    inset 3px 3px 0 rgba(15, 56, 15, 0.18),
    inset -2px -2px 0 rgba(255, 255, 255, 0.18);

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: repeating-linear-gradient(
      to bottom,
      rgba(15, 56, 15, 0.08) 0 1px,
      transparent 1px 3px
    );
    z-index: 2;
  }

  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    height: 40%;
    pointer-events: none;
    background: linear-gradient(
      to bottom,
      transparent,
      rgba(155, 188, 15, 0.12),
      transparent
    );
    animation: ${scan} 7s linear infinite;
    z-index: 2;
  }

  > * {
    position: relative;
    z-index: 1;
    height: ${(props) => (props.$tall ? "auto" : "100%")};
    min-height: ${(props) => (props.$tall ? "100%" : "0")};
  }

  @media (max-width: 760px) {
    min-height: ${(props) => (props.$tall ? "440px" : "310px")};
    height: ${(props) => (props.$tall ? "auto" : "310px")};
  }
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px 8px;
`;

const DPad = styled.div`
  width: 90px;
  height: 90px;
  display: grid;
  grid-template-areas:
    ". up ."
    "left center right"
    ". down .";
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: 1fr 1fr 1fr;
  filter: drop-shadow(2px 3px 0 rgba(0, 0, 0, 0.35));
`;

const DPadBtn = styled.div<{ $area: string }>`
  grid-area: ${(props) => props.$area};
  background: #1b1b1b;
  border: 0;
  padding: 0;
  box-shadow: inset 0 2px 0 #555, inset 0 -3px 0 #000;
  min-width: 0;
  cursor: pointer;

  &:hover {
    background: #2a2a2a;
  }

  &:active {
    box-shadow: inset 0 2px 0 #000;
    background: #111;
  }
`;

const DPadCenter = styled.div`
  grid-area: center;
  background: #1b1b1b;
  box-shadow: inset 0 0 0 3px #111;
`;

const ButtonsGroup = styled.div`
  display: flex;
  gap: 14px;
  margin-right: 8px;
`;

const ActionButton = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #5a5a5a, #1a1a1a 70%);
  border: 2px solid #0a0a0a;
  box-shadow: 1px 3px 0 #000, inset 0 2px 0 rgba(255, 255, 255, 0.2);
  padding: 0;

  &&:hover {
    background: radial-gradient(circle at 35% 30%, #6a6a6a, #222 70%);
  }

  &&:active {
    transform: translateY(1px);
    box-shadow: 0 1px 0 #000;
  }
`;

const LowerRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 20px 6px;
`;

const PillRow = styled.div`
  display: flex;
  gap: 12px;
`;

const Pill = styled.div`
  width: 36px;
  height: 8px;
  background: #1a1a1a;
  border-radius: 8px;
  box-shadow: inset 0 -1px 0 #000, 0 1px 0 rgba(255, 255, 255, 0.15);
`;

const Speaker = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 6px);
  gap: 5px;
  opacity: 0.85;
  margin-top: 16px;
  align-self: flex-start;
`;

const Hole = styled.span`
  width: 6px;
  height: 6px;
  background: ${device.redDeep};
  border-radius: 50%;
  box-shadow: inset 0 1px 1px #000;
`;

const Hinge = styled.div`
  width: 28px;
  flex-shrink: 0;
  margin: -18px 4px;
  background: linear-gradient(
    90deg,
    ${device.redDeep} 0%,
    ${device.redLight} 22%,
    ${device.red} 50%,
    ${device.redDark} 78%,
    ${device.redDeep} 100%
  );
  position: relative;
  box-shadow: inset 2px 0 0 rgba(255, 255, 255, 0.15), inset -2px 0 0 rgba(0, 0, 0, 0.25);

  &::before,
  &::after {
    content: "";
    position: absolute;
    left: 50%;
    width: 18px;
    height: 18px;
    transform: translateX(-50%);
    background: radial-gradient(circle at 35% 35%, #ddd 0 20%, #777 45%, #333 100%);
    border: 2px solid ${device.redDeep};
    border-radius: 50%;
  }

  &::before {
    top: 22%;
  }

  &::after {
    bottom: 18%;
  }

  @media (max-width: 760px) {
    width: auto;
    height: 22px;
    margin: 8px -16px;

    &::before,
    &::after {
      top: 50%;
      transform: translate(-50%, -50%);
      width: 14px;
      height: 14px;
    }

    &::before {
      left: 22%;
    }

    &::after {
      left: 78%;
      bottom: auto;
    }
  }
`;

const Credits = styled.div`
  font-size: 8px;
  color: #8aa0b8;
  text-align: center;
  line-height: 1.8;
  max-width: 720px;

  a {
    color: ${gb.lightest};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

function Pokedex() {
  const [selected, setSelected] = useState<PokemonSummary | null>(null);
  const { move, clearSearch } = usePokedexNav();

  return (
    <AppContainer>
      <PokedexFrame>
        <Panel>
          <TopRow>
            <Lens aria-hidden="true" />
            <Leds>
              <Led $color="#ff4d4d" />
              <Led $color="#ffd166" />
              <Led $color="#7bed9f" />
            </Leds>
          </TopRow>
          <ScreenBezel>
            <Lcd>
              <PokemonList selectedId={selected?.id ?? null} onSelect={setSelected} />
            </Lcd>
          </ScreenBezel>
          <Controls>
            <DPad>
              <DPadBtn $area="up" role="button" tabIndex={0} aria-label="Up" onClick={() => move("up")} />
              <DPadBtn $area="left" role="button" tabIndex={0} aria-label="Previous page" onClick={() => move("left")} />
              <DPadBtn $area="right" role="button" tabIndex={0} aria-label="Next page" onClick={() => move("right")} />
              <DPadBtn $area="down" role="button" tabIndex={0} aria-label="Down" onClick={() => move("down")} />
              <DPadCenter />
            </DPad>
            <ButtonsGroup>
              <ActionButton
                aria-label="Clear search"
                onClick={clearSearch}
              />
              <ActionButton
                aria-label="Select"
                onClick={() =>
                  window.open(
                    "https://www.youtube.com/watch?v=QDia3e12czc",
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
              />
            </ButtonsGroup>
          </Controls>
          <LowerRow>
            <PillRow>
              <Pill />
              <Pill />
            </PillRow>
          </LowerRow>
        </Panel>
        <Hinge />
        <Panel>
          <TopRow />
          <ScreenBezel>
            <Lcd $tall>
              <PokemonDetail pokemonId={selected?.id ?? null} pokemonName={selected?.name} />
            </Lcd>
          </ScreenBezel>
          <LowerRow>
            <PillRow>
              <Pill />
              <Pill />
              <Pill />
            </PillRow>
            <Speaker aria-hidden="true">
              {Array.from({ length: 18 }, (_, i) => (
                <Hole key={i} />
              ))}
            </Speaker>
          </LowerRow>
        </Panel>
      </PokedexFrame>
      <Credits>
        Built by <a href="mailto:thearvindas@gmail.com">Arvin</a> using React +
        Vite + TypeScript + Styled Components +{" "}
        <a href="https://pokeapi.co" target="_blank" rel="noopener noreferrer">
          PokéAPI
        </a>{" "}
        +{" "}
        <a href="https://cursor.sh" target="_blank" rel="noopener noreferrer">
          Cursor IDE
        </a>
      </Credits>
    </AppContainer>
  );
}

function App() {
  return (
    <Router>
      <PokedexNavProvider>
        <Pokedex />
      </PokedexNavProvider>
    </Router>
  );
}

export default App;
